import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setTitle } from '../../redux/title/action';
import {
  InfoHeaderStyledContainer,
  RegisterStyledContainer,
} from '../../components/container/styles';
import { TextSmall, TextXSMall } from '../../components/text';
import { useGetClaimList } from './hooks/useGetClaimList';
import { RenderForm } from '../../components/forms';
import {
  CLAIM_FORM_TOP,
  CLAIM_FORM_BREAKDOWN_SUB,
  CLAIM_FORM_MOBILE_SELECT,
  FORM_ADDRESS,
  FORM_PIC,
  FORM_SHOW_IMAGE_CLAIM,
} from './form';
import { color } from '../../resources';
import { Checkbox, Row, Spin, Col } from 'antd';
import accountNumber from '../../assets/images/account_number.jpg';
import { ImageTheme } from '../../components/images';
import { ContainerButton } from '../../styles/global-style';
import { ButtonTheme } from '../../components/buttons';
import React from 'react';
import { breakdownSubQuery } from '../../services/react-query/optionMasterQuery';

export const ClaimScene = () => {
  const dispatch = useDispatch();
  const warrantyDataFromStore = useSelector((state) => state.warrantyData.data);
  const parsedData = useMemo(() => {
    if (warrantyDataFromStore) {
      return warrantyDataFromStore;
    }
    const raw = typeof window !== 'undefined' ? localStorage.getItem('warrantyData') : null;
    if (!raw) {
      return null;
    }
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }, [warrantyDataFromStore]);

  const warrantyMobilesRows = useMemo(() => {
    const w = parsedData?.warranty;
    if (!w) {
      return [];
    }
    const list = Array.isArray(w.mobiles) ? [...w.mobiles] : [];
    if (list.length > 0) {
      return list.sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0));
    }
    if (w.mobileBrandName || w.mobileModelName) {
      return [
        {
          seq: 1,
          warrantyMobileId: 0,
          mobileBrandName: w.mobileBrandName || '–',
          mobileModelName: w.mobileModelName || '–',
        },
      ];
    }
    return [];
  }, [parsedData]);

  const [addressOptions, setAddressOptions] = useState([]);
  const [breakdownOptions, setBreakdownOptions] = useState([]);
  const [channelClaimOptions, setChannelClaimOptions] = useState([]);
  const [productType, setProductType] = useState([]);
  const [isAccepted, setIsAccepted] = useState(false);
  const showOldAddress = true;

  const {
    control,
    getValues,
    setValue,
    watch,
    onCallZipCode,
    provinceOptions,
    districtOptions,
    subDistrictOptions,
    addressFullOptions,
    onChangeAddress,
    isLoadingZipCode,
    handleSubmit,
    onSubmit,
    isLoadingToSave,
  } = useGetClaimList();

  const warrantyMobileIdsWatch = watch('warrantyMobileIds');
  const breakdownId = watch('breakdownId');

  useEffect(() => {
    if (breakdownId) {
      const selectedBreakdown = breakdownOptions.find((item) => item.value === breakdownId);
      setProductType(selectedBreakdown.productType);
    }
  }, [breakdownId]);
  const { data: breakdownSubOptions, isLoading: isLoadingBreakdownSub } =
    breakdownSubQuery(productType);

  const warrantyMobileSelectOptions = useMemo(() => {
    const w = parsedData?.warranty;
    const list = Array.isArray(w?.mobiles) ? [...w.mobiles] : [];
    const opts = list
      .filter((m) => m.warrantyMobileId > 0)
      .sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0))
      .map((m) => ({
        label: `${m.mobileBrandName || '–'} / ${m.mobileModelName || '–'}`.trim(),
        value: m.warrantyMobileId,
      }));
    opts.push({ label: 'อื่น ๆ', value: -1 });
    return opts;
  }, [parsedData]);

  useEffect(() => {
    setValue('breakdownSubId', null);
  }, [breakdownId, setValue]);

  useEffect(() => {
    const arr = Array.isArray(warrantyMobileIdsWatch) ? warrantyMobileIdsWatch : [];
    if (!arr.includes(-1)) {
      setValue('warrantyMobileOtherReason', '');
    }
  }, [warrantyMobileIdsWatch, setValue]);

  useEffect(() => {
    if (parsedData) {
      setAddressOptions(
        parsedData?.addresses?.map((item) => ({
          label: item.addressFullText,
          value: item.customerAddressId,
        })) || [],
      );

      setBreakdownOptions(
        parsedData?.warranty?.breakdowns?.map((item) => ({
          label: item.breakdownNameTh,
          value: item.breakdownId,
          productType: item.productType,
        })) || [],
      );

      setChannelClaimOptions(
        parsedData?.warranty?.channelClaims?.map((item) => ({
          label: item.channelClaimNameLineOa,
          value: item.channelClaimId,
        })) || [],
      );
    }
  }, [parsedData]);

  useEffect(() => {
    if (warrantyDataFromStore) {
      localStorage.setItem('warrantyData', JSON.stringify(warrantyDataFromStore));
    }
  }, [warrantyDataFromStore]);

  useEffect(() => {
    dispatch(setTitle('แจ้งเคลมสินค้า'));
  }, [dispatch]);

  useEffect(() => {
    if (parsedData?.warranty) {
      const w = parsedData.warranty;
      setValue('warrantyCode', w.warrantyCode);
      setValue('warrantyId', w.warrantyId);

      if (w.channelClaims?.length > 0) {
        setValue('channelClaimId', w.channelClaims[0].channelClaimId);
      }
    }
  }, [setValue, parsedData]);

  const handleCheckboxChange = (e) => {
    setIsAccepted(e.target.checked);
  };

  return (
    <RegisterStyledContainer>
      <Spin spinning={isLoadingZipCode || isLoadingToSave || isLoadingBreakdownSub}>
        <InfoHeaderStyledContainer>
          <TextSmall text="กรุณากรอกข้อมูลเพื่อเคลมสินค้า" style={{ color: '#8c6e19' }} bold />
        </InfoHeaderStyledContainer>
        <Row gutter={[0, 10]} justify="center" style={{ padding: 20, paddingBottom: 50 }}>
          <Col span={24}>
            <RenderForm
              control={control}
              setValue={setValue}
              getValues={getValues}
              forms={CLAIM_FORM_TOP({ breakdownOptions })}
            />
          </Col>
          <Col span={24}>
            <RenderForm
              control={control}
              setValue={setValue}
              getValues={getValues}
              forms={CLAIM_FORM_BREAKDOWN_SUB({ breakdownSubOptions })}
            />
          </Col>
          {warrantyMobilesRows.length > 0 && (
            <Col span={24} style={{ paddingLeft: 4, paddingRight: 4 }}>
              <TextXSMall text="มือถือที่ลงทะเบียนรับประกัน" bold />
              {warrantyMobilesRows.map((m, idx) => (
                <Row
                  key={
                    m.warrantyMobileId != null && m.warrantyMobileId !== 0
                      ? m.warrantyMobileId
                      : `m-${idx}`
                  }
                  style={{ marginTop: 8 }}
                  gutter={[0, 4]}
                >
                  <Col span={24}>
                    <TextSmall
                      text={
                        <span style={{ color: '#595959' }}>
                          {warrantyMobilesRows.length > 1 ? `รายการที่ ${m.seq ?? idx + 1} — ` : ''}
                          <strong>{m.mobileBrandName || '–'}</strong>
                          {' / '}
                          {m.mobileModelName || '–'}
                        </span>
                      }
                    />
                  </Col>
                </Row>
              ))}
            </Col>
          )}
          <Col span={24}>
            <RenderForm
              control={control}
              setValue={setValue}
              getValues={getValues}
              forms={CLAIM_FORM_MOBILE_SELECT({
                warrantyMobileOptions: warrantyMobileSelectOptions,
                watch,
              })}
            />
          </Col>
          <Col span={24}>
            <RenderForm
              control={control}
              setValue={setValue}
              getValues={getValues}
              forms={FORM_SHOW_IMAGE_CLAIM()}
            />
          </Col>

          <Col span={24}>
            <RenderForm
              control={control}
              setValue={setValue}
              getValues={getValues}
              onChange={onChangeAddress}
              forms={FORM_ADDRESS({
                showOldAddress,
                watch,
                channelClaimOptions,
                addressOptions,
                onCallZipCode,
                provinceOptions,
                districtOptions,
                subDistrictOptions,
                addressFullOptions,
              })}
            />
          </Col>

          <Col span={24}>
            <Row justify="end">
              <TextSmall
                text="ชำระเงินค่าดำเนินการและค่าจัดส่ง 40 บาท"
                style={{ color: color.redRizz }}
              />
            </Row>
          </Col>

          <Col span={24} style={{ textAlign: 'center' }}>
            <ImageTheme src={accountNumber} style={{ width: 300 }} />
          </Col>

          <Col span={24} style={{ textAlign: 'center' }}>
            <Row justify="center">
              <RenderForm
                control={control}
                setValue={setValue}
                getValues={getValues}
                forms={FORM_PIC()}
              />
            </Row>
            <TextXSMall text="กรุณาแนบไฟล์ (.png, .jpg, .jpeg, .heic)" bold align="center" />
          </Col>

          <Col span={24}>
            <Row justify="center">
              <Checkbox checked={isAccepted} onChange={handleCheckboxChange}>
                <span style={{ color: color.redRizz }}>
                  ข้าพเจ้าขอรับรองว่าข้อมูลข้างต้นเป็นความจริงทุกประการ
                  และข้อมูลดังกล่าวมีผลทางกฎหมาย
                </span>
              </Checkbox>
            </Row>
          </Col>

          <Col span={24}>
            <Row justify="center">
              <ContainerButton center>
                <ButtonTheme
                  useFor="CLAIM"
                  onClick={handleSubmit(onSubmit)}
                  disabled={!isAccepted}
                />
              </ContainerButton>
            </Row>
          </Col>
        </Row>
      </Spin>
    </RegisterStyledContainer>
  );
};
